import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Log environment variables to help with debugging
console.log('Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
console.log('Supabase Anon Key length:', import.meta.env.VITE_SUPABASE_ANON_KEY?.length || 0);

if (!import.meta.env.VITE_SUPABASE_URL) {
  throw new Error('Missing environment variable: VITE_SUPABASE_URL');
}

if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
  throw new Error('Missing environment variable: VITE_SUPABASE_ANON_KEY');
}

// Create a single instance of the Supabase client
export const supabase = createClient<Database>(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'supabase.auth.token',
      storage: {
        getItem: (key) => {
          try {
            const item = localStorage.getItem(key);
            return item;
          } catch (error) {
            console.error('Error getting auth from localStorage', error);
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            localStorage.setItem(key, value);
          } catch (error) {
            console.error('Error storing auth in localStorage', error);
          }
        },
        removeItem: (key) => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.error('Error removing auth from localStorage', error);
          }
        }
      }
    },
    global: {
      headers: {
        'X-Client-Info': 'nutfreenomad-web'
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    },
    // Reduce network timeouts for mobile
    fetch: (url, options) => {
      const controller = new AbortController();
      const { signal } = controller;
      
      // Set a timeout for all fetch requests
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 10000); // 10 second timeout
      
      return fetch(url, { ...options, signal })
        .finally(() => {
          clearTimeout(timeoutId);
        });
    }
  }
);

// Add debug listener for auth state changes
let authStateChangeCount = 0;
supabase.auth.onAuthStateChange((event, session) => {
  authStateChangeCount++;
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] Auth state changed (${authStateChangeCount}): ${event}`, session?.user?.id || 'No user');
  
  // Force update localStorage to ensure persistence
  if (session) {
    try {
      const currentData = localStorage.getItem('supabase.auth.token');
      if (currentData) {
        // Re-save to ensure it's properly stored
        const parsedData = JSON.parse(currentData);
        parsedData.currentSession = session;
        localStorage.setItem('supabase.auth.token', JSON.stringify(parsedData));
        console.log(`[${timestamp}] Updated localStorage with session data`);
      }
    } catch (e) {
      console.error('Error updating localStorage:', e);
    }
  }
});

// Preload auth state as early as possible
(async () => {
  try {
    const startTime = performance.now();
    const { data } = await supabase.auth.getSession();
    const endTime = performance.now();
    console.log(`Preloaded auth state in ${(endTime - startTime).toFixed(2)}ms:`, data.session ? 'Authenticated' : 'Not authenticated');
  } catch (error) {
    console.error('Failed to preload auth state:', error);
  }
})();

// Export a function to check if the auth is initialized
export const isAuthInitialized = () => {
  try {
    return !!localStorage.getItem('supabase.auth.token');
  } catch (e) {
    return false;
  }
};

// Add a helper function to check if a user is authenticated
export const isUserAuthenticated = () => {
  try {
    const authData = localStorage.getItem('supabase.auth.token');
    if (!authData) return false;
    
    const parsedData = JSON.parse(authData);
    if (!parsedData?.currentSession?.access_token) return false;
    
    // Check if token is expired
    const expiresAt = parsedData.currentSession.expires_at;
    if (expiresAt && expiresAt < Math.floor(Date.now() / 1000)) return false;
    
    return true;
  } catch (e) {
    console.error('Error checking auth status:', e);
    return false;
  }
};